/** What each read behind an Administration section asks the server for, as internal/api/api.go
 *  gates it. A screen reads only what this says the person may, so a visit writes no refusals. */
export const READS = {
  branchConfig: { any: ['profiles.manage', 'catalogue.manage', 'branch.manage'] },
  operationProfiles: { any: ['profiles.manage', 'catalogue.manage', 'branch.manage'] },
  layouts: ['layout.manage'],
  appointmentPolicy: ['appointment.manage'],
  services: ['catalogue.manage'],
  queues: [],
  profiles: [],
  counters: ['catalogue.manage'],
  units: ['catalogue.manage'],
  queueAlerts: ['catalogue.manage'],
  outcomes: ['catalogue.manage'],
  marks: ['catalogue.manage'],
  deliveredServices: ['catalogue.manage'],
  fields: ['ticket.read'],
  notifications: ['organisation.manage'],
  deliveries: ['organisation.manage'],
  screenApps: ['catalogue.manage'],
  // The login reads every figure (report.read); System administration's (organisation.manage).
  dataFeed: ['organisation.manage', 'report.read'],
} as const satisfies Record<string, readonly string[] | { readonly any: readonly string[] }>

export type ReadKey = keyof typeof READS

/** A list needs every permission in it; `{ any }` needs one of them. */
export function mayRead(permissions: readonly string[], read: ReadKey): boolean {
  const need = READS[read]
  if ('any' in need) return need.any.some((p) => permissions.includes(p))
  return (need as readonly string[]).every((p) => permissions.includes(p))
}

/** A section's reads: without every one in needs it is not offered; optional ones are skipped
 *  by the screen itself when not allowed, and the part they feed is left out. */
export const SECTION_READS: Record<string, { needs: ReadKey[]; optional?: ReadKey[] }> = {
  alerts: { needs: ['queueAlerts', 'queues'] },
  services: { needs: ['services', 'deliveredServices'], optional: ['layouts'] },
  outcomes: { needs: ['outcomes'] },
  marks: { needs: ['marks'] },
  options: { needs: [] },
  wording: { needs: [] },
  languages: { needs: [] },
  fields: { needs: ['fields'], optional: ['appointmentPolicy'] },
  notifications: { needs: ['notifications', 'fields'] },
  deliveries: { needs: ['deliveries'] },
  screenApps: { needs: ['screenApps'] },
  branches: { needs: [], optional: ['branchConfig', 'layouts'] },
  dataFeed: { needs: ['dataFeed'] },
  opProfiles: { needs: ['operationProfiles'] },
  equipment: { needs: ['operationProfiles'], optional: ['layouts'] },
}
